import React from "react";
import {
  Card,
  CardContent,
  Typography,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Stack,
} from "@mui/material";
import StatusChip from "../common/StatusChip";
import { InvoiceResponseDto } from "../../shared/dtos/invoice/InvoiceResponseDto";
import { formatPlainNumber } from "../../utils/appUtils";

interface InvoiceDetailsTabProps {
  invoice: InvoiceResponseDto;
}

const InvoiceDetailsTab: React.FC<InvoiceDetailsTabProps> = ({ invoice }) => {
  const { header, items, payments, taxBreakdowns, charges } = invoice;

  return (
    <Box
      sx={{
        display: "grid",
        gap: 3,
        gridTemplateColumns: { xs: "1fr", md: "1fr 2fr" },
        alignItems: "start",
      }}
    >
      {/* --- LEFT COLUMN: Customer & Company Info --- */}
      <Box>
        <Stack spacing={3}>
          <Card
            elevation={0}
            sx={{
              border: "1px solid",
              borderColor: "divider",
              bgcolor: "background.paper",
            }}
          >
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: "bold" }}>
                Billed To
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <Typography
                variant="body2"
                sx={{ color: "text.secondary", mb: 0.5 }}
              >
                Customer Name
              </Typography>
              <Typography
                variant="body1"
                gutterBottom
                sx={{ fontWeight: "bold" }}
              >
                {invoice.customerName}
              </Typography>

              <Typography
                variant="body2"
                sx={{ color: "text.secondary", mt: 2, mb: 0.5 }}
              >
                Customer Contact
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {/* Fallback assumed from previous logic since it's missing in exact DTO */}
                {"Not Provided"}
              </Typography>

              <Typography
                variant="body2"
                sx={{ color: "text.secondary", mt: 2, mb: 0.5 }}
              >
                Sale Type
              </Typography>
              <StatusChip status={invoice.saleType} />
            </CardContent>
          </Card>

          <Card
            elevation={0}
            sx={{
              border: "1px solid",
              borderColor: "divider",
              bgcolor: "background.paper",
            }}
          >
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: "bold" }}>
                Issued By
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                {header?.companySnapshot?.companyName || "Company Name"}
              </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                PIN: {header?.companySnapshot?.taxId || "N/A"}
              </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                {header?.companySnapshot?.address || "Address"}
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "text.secondary", mt: 1 }}
              >
                Tel: {header?.companySnapshot?.phone || "N/A"}
              </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                Email: {header?.companySnapshot?.email || "N/A"}
              </Typography>
            </CardContent>
          </Card>
        </Stack>
      </Box>

      {/* --- RIGHT COLUMN: Items & Financials --- */}
      <Box>
        <Card
          elevation={0}
          sx={{
            border: "1px solid",
            borderColor: "divider",
            mb: 3,
            bgcolor: "background.paper",
          }}
        >
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: "bold" }}>
              Invoice Items
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead sx={{ bgcolor: "background.default" }}>
                  <TableRow>
                    <TableCell>Description</TableCell>
                    <TableCell align="right">Qty</TableCell>
                    <TableCell align="right">Unit Price</TableCell>
                    <TableCell align="right">Disc/Unit</TableCell>
                    <TableCell align="right">Tax Amount</TableCell>
                    <TableCell align="right">Line Total</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items?.length > 0 ? (
                    items.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell>
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: "bold" }}
                          >
                            {item.name}
                          </Typography>
                          {item.taxCode && (
                            <Typography
                              variant="caption"
                              sx={{ color: "text.secondary" }}
                            >
                              Tax Code: {item.taxCode}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="right">{item.qty}</TableCell>
                        <TableCell align="right">
                          {formatPlainNumber(item.price)}
                        </TableCell>
                        <TableCell align="right" sx={{ color: "error.main" }}>
                          {item.discount > 0
                            ? formatPlainNumber(item.discount)
                            : "-"}
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{ color: "text.secondary" }}
                        >
                          {formatPlainNumber(item.taxAmount)}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: "bold" }}>
                          {formatPlainNumber(item.amount)}
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        No items found on this invoice.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>

        {/* Financial Summary & Payments Grid */}
        <Box
          sx={{
            display: "grid",
            gap: 3,
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            alignItems: "stretch",
          }}
        >
          <Box>
            <Card
              elevation={0}
              sx={{
                border: "1px solid",
                borderColor: "divider",
                height: "100%",
                bgcolor: "background.paper",
              }}
            >
              <CardContent>
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{ fontWeight: "bold" }}
                >
                  Payment History
                </Typography>

                {payments && payments.length > 0 ? (
                  <TableContainer>
                    <Table size="small">
                      <TableBody>
                        {payments.map((payment) => (
                          <TableRow key={payment.id}>
                            <TableCell sx={{ borderBottom: "none", pl: 0 }}>
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: "bold" }}
                              >
                                {payment.paymentMethodName}
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: "text.secondary",
                                  display: "block",
                                }}
                              >
                                {new Date(payment.createdAt).toLocaleString()}
                              </Typography>
                              {payment.referenceCode && (
                                <Typography
                                  variant="caption"
                                  sx={{ color: "secondary.main" }}
                                >
                                  Ref: {payment.referenceCode}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{
                                borderBottom: "none",
                                pr: 0,
                                fontWeight: "bold",
                                color: "success.main",
                              }}
                            >
                              {formatPlainNumber(payment.amount)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Typography
                    variant="body2"
                    sx={{ color: "text.secondary", mb: 3 }}
                  >
                    No payments have been recorded yet.
                  </Typography>
                )}

                {/* Inclusive Charges Breakdown */}
                {charges && charges.length > 0 && (
                  <Box
                    sx={{
                      mt: 3,
                      pt: 2,
                      borderTop: "1px dashed",
                      borderColor: "divider",
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      gutterBottom
                      sx={{ color: "text.secondary", fontWeight: "bold" }}
                    >
                      Inclusive Charges
                    </Typography>
                    {charges.map((charge, idx) => (
                      <Box
                        key={idx}
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          mb: 0.5,
                        }}
                      >
                        <Typography variant="body2">
                          {charge.name} ({charge.rate}%)
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                          {formatPlainNumber(charge.amount)}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                )}

                {/* Tax Breakdown */}
                {taxBreakdowns && taxBreakdowns.length > 0 && (
                  <Box
                    sx={{
                      mt: charges && charges.length > 0 ? 1 : 3,
                      pt: charges && charges.length > 0 ? 1 : 2,
                      borderTop:
                        charges && charges.length > 0 ? "none" : "1px dashed",
                      borderColor: "divider",
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      gutterBottom
                      sx={{ color: "text.secondary", fontWeight: "bold" }}
                    >
                      Tax Breakdown
                    </Typography>
                    {taxBreakdowns.map((tax) => (
                      <Box
                        key={tax.id}
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          mb: 0.5,
                        }}
                      >
                        <Typography variant="body2">
                          {tax.name} ({tax.rate}%)
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                          {formatPlainNumber(tax.taxAmount)}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                )}
              </CardContent>
            </Card>
          </Box>

          <Box>
            <Card
              elevation={0}
              sx={{
                border: "1px solid",
                borderColor: "divider",
                bgcolor: "background.default",
                height: "100%",
              }}
            >
              <CardContent>
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{ fontWeight: "bold" }}
                >
                  Summary
                </Typography>

                {/* Currency and Exchange Rate */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 1,
                    mt: 2,
                  }}
                >
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    Currency
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: "bold", color: "secondary.main" }}
                  >
                    {invoice.currencyCode || "KES"}
                  </Typography>
                </Box>

                {Number(invoice.exchangeRate) !== 1 && (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 1,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ color: "text.secondary" }}
                    >
                      Exchange Rate
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: "bold", color: "secondary.main" }}
                    >
                      {invoice.exchangeRate}
                    </Typography>
                  </Box>
                )}

                <Divider sx={{ my: 1 }} />

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 1,
                  }}
                >
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    Subtotal (Excl. Tax)
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {formatPlainNumber(invoice.subtotal)}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 1,
                  }}
                >
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    Total Discount
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 500, color: "error.main" }}
                  >
                    - {formatPlainNumber(invoice.totalDiscount || 0)}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 1,
                  }}
                >
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    Total Tax
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {formatPlainNumber(invoice.totalTax)}
                  </Typography>
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* CURRENCY ADDED ONLY HERE */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 2,
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                    Invoice Total
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: "bold", color: "secondary.main" }}
                  >
                    {invoice.currencyCode} {formatPlainNumber(invoice.total)}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 1,
                  }}
                >
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    Amount Paid
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: "bold", color: "success.main" }}
                  >
                    {formatPlainNumber(invoice.amountPaid)}
                  </Typography>
                </Box>

                {/* AND HERE */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    p: 1,
                    bgcolor: "background.paper",
                    borderRadius: 1,
                    mt: 1,
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                    Balance Due
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: "bold",
                      color:
                        invoice.total - invoice.amountPaid > 0
                          ? "error.main"
                          : "text.primary",
                    }}
                  >
                    {invoice.currencyCode}{" "}
                    {formatPlainNumber(invoice.total - invoice.amountPaid)}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default InvoiceDetailsTab;
